import React from "react";
import RichEditor from "../../../components/dashboard/RichEditor";
import { AnnotatedBody } from "../../anchored-comments";
import { InternalGistLinkComposer } from "./InternalGistLinkComposer";
import { useCanonicalAnchoredComments } from "../hooks/useCanonicalAnchoredComments";
import styles from "../SubjectSinglePage.module.css";
import type { GistItem, SubjectItem } from "../model/types";
import {
  canUserEditGist,
  formatDate,
  getAuthorDetails,
  getFullReaderText,
  getLatestEdit,
  getLatestEditBody,
  getSummaryText,
  normalizeRenderedHtml,
  stripHtml,
} from "../model/subjectSingleModel";

type GistReadArticleProps = {
  gist: GistItem;
  user: any;
  withMarkers?: boolean;
  summaryDraft: string;
  bodyDraft: string;
  readerEditError: string | null;
  isSavingReaderEdit: boolean;
  isSummaryEditing: boolean;
  isBodyEditing: boolean;
  onBeginSummaryEdit: (gist: GistItem) => void;
  onBeginBodyEdit: (gist: GistItem) => void;
  onCancelReaderEdit: () => void;
  onSaveReaderEdit: (gist: GistItem) => void;
  onSummaryDraftChange: React.Dispatch<React.SetStateAction<string>>;
  onBodyDraftChange: React.Dispatch<React.SetStateAction<string>>;
  onOpenCommentWorkspaceForAnchor?: (gist: GistItem, anchorId: number) => void;
  activeWorkspaceAnchorId?: number | null;
  canCreateLinkedDraft?: boolean;
  onCreateLinkedDraft?: (gist: GistItem) => void;
  linkableGists?: GistItem[];
  linkableSubjects?: SubjectItem[];
  currentSubjectId?: number | null;
  getInternalGistLinkUrl?: (gist: GistItem) => string;
};

function shouldIgnoreBodyDoubleClick(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;

  return Boolean(
    target.closest(
      [
        "button",
        "a",
        "input",
        "textarea",
        "select",
        "[contenteditable='true']",
        ".anchored-comment-marker",
        ".anchored-comment-hover-popover",
        ".anchored-comment-selection-composer",
        ".anchored-comment-thread-panel",
        ".comment-explorer",
        ".ql-toolbar",
      ].join(", "),
    ),
  );
}

export const GistReadArticle: React.FC<GistReadArticleProps> = ({
  gist,
  user,
  withMarkers = false,
  summaryDraft,
  bodyDraft,
  readerEditError,
  isSavingReaderEdit,
  isSummaryEditing,
  isBodyEditing,
  onBeginSummaryEdit,
  onBeginBodyEdit,
  onCancelReaderEdit,
  onSaveReaderEdit,
  onSummaryDraftChange,
  onBodyDraftChange,
  onOpenCommentWorkspaceForAnchor,
  activeWorkspaceAnchorId = null,
  canCreateLinkedDraft = false,
  onCreateLinkedDraft,
  linkableGists = [],
  linkableSubjects = [],
  currentSubjectId = null,
  getInternalGistLinkUrl,
}) => {
  const summary = getSummaryText(gist);
  const bodyHtml = getLatestEditBody(gist);
  const activeEdit = getLatestEdit(gist);
  const author = getAuthorDetails(gist);
  const canEditReaderContent = canUserEditGist(gist, user);
  const titleId = `single-gist-reader-title-${gist.id}`;
  const normalizedBodyHtml = bodyHtml ? normalizeRenderedHtml(bodyHtml) : "";
  const annotationsEnabled = Boolean(withMarkers && !isBodyEditing && normalizedBodyHtml);
  const anchoredComments = useCanonicalAnchoredComments({
    gistId: gist.id,
    editId: activeEdit?.id ?? null,
    enabled: annotationsEnabled,
    user,
  });

  const handleBodyDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (shouldIgnoreBodyDoubleClick(event.target)) return;
    onBeginBodyEdit(gist);
  };

  return (
    <article className={styles.gistArticle} aria-labelledby={titleId}>
      <div className={styles.gistArticleTopline}>
        <h2 id={titleId}>{gist.title}</h2>
        <div className={styles.gistArticleToplineActions}>
          {isBodyEditing && getInternalGistLinkUrl && (
            <InternalGistLinkComposer
              currentGistId={gist.id}
              gists={linkableGists}
              subjects={linkableSubjects}
              currentSubjectId={currentSubjectId ?? gist.subjectId ?? null}
              bodyDraft={bodyDraft}
              disabled={isSavingReaderEdit}
              getGistLinkUrl={getInternalGistLinkUrl}
              onBodyDraftChange={onBodyDraftChange}
            />
          )}
          {onCreateLinkedDraft && (
            <button
              type="button"
              className={styles.articleLinkedDraftButton}
              disabled={!canCreateLinkedDraft}
              title={
                canCreateLinkedDraft
                  ? "Create a private child draft linked to this gist"
                  : "Login required"
              }
              onClick={() => onCreateLinkedDraft(gist)}
            >
              + Linked draft
            </button>
          )}
        </div>
      </div>
      <div className={styles.gistArticleMeta}>
        <img
          src={author.image}
          className={styles.authorAvatar}
          alt="Author avatar"
        />
        <div className={styles.authorCopy}>
          <span className={styles.authorName}>{author.name}</span>
          <span className={styles.authorDate}>{formatDate(author.date)}</span>
        </div>
        {gist.publishedAt && (
          <span className={styles.publishedMeta}>
            Published {formatDate(gist.publishedAt)}
          </span>
        )}
      </div>

      <section className={styles.gistSummaryBlock} aria-label="Gist summary">
        <strong>Summary</strong>
        {isSummaryEditing ? (
          <div>
            <textarea
              className={styles.summaryEditor}
              value={summaryDraft}
              onChange={(event) =>
                onSummaryDraftChange(event.target.value.slice(0, 300))
              }
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  onCancelReaderEdit();
                } else if (
                  event.key === "Enter" &&
                  (event.ctrlKey || event.metaKey)
                ) {
                  onSaveReaderEdit(gist);
                }
              }}
              rows={4}
              autoFocus
            />
            <div
              className={styles.readerEditActions}
              aria-label="Summary editing actions"
            >
              {readerEditError && (
                <span className={styles.readerEditError}>
                  {readerEditError}
                </span>
              )}
              <button
                type="button"
                onClick={onCancelReaderEdit}
                disabled={isSavingReaderEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onSaveReaderEdit(gist)}
                disabled={isSavingReaderEdit}
              >
                {isSavingReaderEdit ? "Saving..." : "Save as new version"}
              </button>
            </div>
          </div>
        ) : (
          <p
            className={styles.gistSummaryText}
            onDoubleClick={() => onBeginSummaryEdit(gist)}
            title={
              canEditReaderContent ? "Double-click to edit summary" : undefined
            }
            style={{ cursor: canEditReaderContent ? "pointer" : "default" }}
          >
            {summary}
          </p>
        )}
      </section>

      <section className={styles.gistBodyBlock} aria-label="Gist body">
        <strong>Body</strong>
        <div className={styles.bodyWithMarkers}>
          <div
            className={`${styles.gistBodyRich} ${canEditReaderContent ? styles.gistBodyEditable : ""}`}
            onDoubleClick={handleBodyDoubleClick}
            title={
              canEditReaderContent ? "Double-click to edit body" : undefined
            }
          >
            {isBodyEditing ? (
              <div
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    onCancelReaderEdit();
                  } else if (
                    event.key === "Enter" &&
                    (event.ctrlKey || event.metaKey)
                  ) {
                    onSaveReaderEdit(gist);
                  }
                }}
              >
                <RichEditor
                  editable={true}
                  content={bodyDraft}
                  setContent={onBodyDraftChange}
                />
                <div
                  className={styles.readerEditActions}
                  aria-label="Body editing actions"
                >
                  {readerEditError && (
                    <span className={styles.readerEditError}>
                      {readerEditError}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={onCancelReaderEdit}
                    disabled={isSavingReaderEdit}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => onSaveReaderEdit(gist)}
                    disabled={isSavingReaderEdit}
                  >
                    {isSavingReaderEdit ? "Saving..." : "Save changes"}
                  </button>
                </div>
              </div>
            ) : normalizedBodyHtml ? (
              annotationsEnabled ? (
                <AnnotatedBody
                  html={normalizedBodyHtml}
                  anchors={anchoredComments.anchors}
                  loading={anchoredComments.anchorsLoading}
                  error={anchoredComments.anchorsError}
                  commentsVisible={withMarkers}
                  gistId={gist.id}
                  editId={activeEdit?.id ?? null}
                  comments={anchoredComments.comments}
                  commentsLoading={anchoredComments.commentsLoading}
                  commentsError={anchoredComments.commentsError}
                  currentUserId={anchoredComments.currentUserId}
                  currentUserRole={anchoredComments.currentUserRole}
                  canCreateAnchoredComment={
                    anchoredComments.canCreateAnchoredComment
                  }
                  onCreateAnchoredComment={
                    anchoredComments.onCreateAnchoredComment
                  }
                  onReply={anchoredComments.onReply}
                  onSoftRemove={anchoredComments.onSoftRemove}
                  onHardDeleteThread={anchoredComments.onHardDeleteThread}
                  onAnchorOpen={
                    onOpenCommentWorkspaceForAnchor
                      ? (anchorId) => onOpenCommentWorkspaceForAnchor(gist, anchorId)
                      : undefined
                  }
                  activeAnchorId={activeWorkspaceAnchorId}
                />
              ) : (
                <RichEditor
                  editable={false}
                  content={normalizedBodyHtml}
                  setContent={() => undefined}
                />
              )
            ) : (
              <p>{stripHtml(getFullReaderText(gist))}</p>
            )}
          </div>
        </div>
      </section>
    </article>
  );
};
